// Inline chat paywall widget — ports messaging.js _showInlinePaywall (parent
// feat/inline-paywall). Rendered inside an assistant bubble when access is
// denied: a list of plan buttons (tap → Tochka checkout) + a "details" link to
// the full pricing screen. Self-contained so Message.jsx needs no wiring.

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { paymentsApi } from '../../api/payments'
import { useAccountSegment } from '../../features/product-segment'
import { checkoutCoordinator, getPaymentChannel, paymentResultMessage } from '../../payments/channel'
import type { PlanId, StoreProduct } from '../../payments/types'
import * as metrica from '../../lib/metrica'
import { discountPercent, formatDuration } from '../../lib/pricing'
import styles from './ChatPaywall.module.css'

const CHAT_PLAN_COPY = {
  session: { subtitle: 'Продолжить начатую работу', footer: 'обсуждение одного вопроса' },
  month: {
    subtitle: 'Системная работа + анализ динамики',
    footer: 'безлимит сессий, отмена в один клик, работа с живым психологом',
  },
  quarter: {
    subtitle: 'Три месяца, чтобы навык закрепился',
    footer: 'безлимит сессий, отмена в один клик, работа с живым психологом',
  },
} as const

// break_up grid (#4480, 17.10): week/month/program, no live-psychologist
// service in the catalog; copy is segment-scoped so humanteq keeps its own.
const CHAT_PLAN_COPY_BREAK_UP = {
  week: { subtitle: 'Попробовать неделю', footer: 'безлимит сессий, отмена в один клик' },
  month: { subtitle: 'Системная работа + анализ динамики', footer: 'безлимит сессий, отмена в один клик' },
  program: { subtitle: 'Полная программа — 12 недель', footer: 'безлимит сессий, отмена в один клик' },
} as const

export default function ChatPaywall() {
  const navigate = useNavigate()
  const channel = getPaymentChannel()
  const { profile } = useAccountSegment()
  const planCopy = profile?.segment === 'break_up' ? CHAT_PLAN_COPY_BREAK_UP : CHAT_PLAN_COPY
  const [payingId, setPayingId] = useState<string | null>(null)
  const [payMessage, setPayMessage] = useState('')
  const payingRef = useRef(false)

  const { data = { plans: [], products: [] as StoreProduct[], catalogError: '' } } = useQuery({
    queryKey: ['pricing', 'plans'],
    queryFn: async () => {
      const data = await paymentsApi.plans()
      const plans = (data.plans || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
      try {
        return { plans, products: await channel.getProducts(), catalogError: '' }
      } catch (error) {
        if (channel.provider !== 'rustore') throw error
        return { plans, products: [] as StoreProduct[], catalogError: paymentResultMessage(error) }
      }
    },
    // The paywall bubble itself is persisted (messagingStore preserves it
    // across reloads), but this query is not — a cold PWA relaunch always
    // starts from an empty QueryClient. Right after relaunch the network/SW
    // aren't always ready yet, so the app-wide default (retry: 1,
    // refetchOnWindowFocus: false) can leave this specific query stuck in an
    // error state — plans stay [] and the buttons never come back. Override
    // both here so this fetch keeps retrying and re-checks on refocus/
    // reconnect, since without that the buttons vanish until something else
    // happens to remount this component.
    retry: 3,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const pay = async (planId: string) => {
    if (payingRef.current) return
    payingRef.current = true
    setPayingId(planId)
    setPayMessage('')
    const claim = checkoutCoordinator.claim(channel, planId as PlanId)
    if (claim.kind === 'busy') {
      setPayMessage(paymentResultMessage({ code: 'purchase_in_progress' }))
      setPayingId(null)
      payingRef.current = false
      return
    }
    if (claim.kind === 'acceptedNew') {
      metrica.reachGoal('tariff_click', { plan: planId })
      metrica.reachGoal(`tariff_click_${planId}`)
    }
    try {
      const outcome = await claim.promise
      setPayMessage(paymentResultMessage(outcome))
    } catch (error) {
      if (channel.provider === 'tochka') {
        navigate('/pricing')
      } else {
        setPayMessage(paymentResultMessage(error))
      }
    } finally {
      setPayingId(null)
      payingRef.current = false
    }
  }

  // No plans yet → route to the full pricing screen (matches original fallback).
  if (!data.plans.length) return null

  const featuredPlan = data.plans.find((p) => p.featured)
  const productsByPlan = new Map(data.products.map((product) => [product.planId, product]))

  return (
    <div className={styles.paywall}>
      <div className={styles.plans}>
        {data.plans.map((plan) => {
          const price = plan.price == null ? '—' : `${plan.price.toLocaleString('ru-RU')} ₽`
          const product = productsByPlan.get(plan.id as PlanId)
          const nativeUnavailable = channel.provider === 'rustore' && !product
          const displayName = channel.provider === 'rustore' ? (product?.title ?? 'Тариф недоступен') : plan.name
          const displayPrice = channel.provider === 'rustore' ? (product?.formattedPrice ?? 'Недоступно') : price
          const fallbackSubtitle = plan.token_based === true ? 'Без ограничения по времени' : formatDuration(plan.duration_days)
          const copy = planCopy[plan.id as keyof typeof planCopy]
          const discount = channel.provider === 'rustore' ? null : discountPercent(plan, featuredPlan)
          return (
            <button
              key={plan.id}
              className={`${styles.plan} ${plan.featured ? styles.featured : ''}`}
              onClick={() => pay(plan.id)}
              disabled={payingId != null || nativeUnavailable}
            >
              <span className={styles.header}>
                <span className={styles.name}>
                  {displayName} – {payingId === plan.id ? '…' : displayPrice}
                </span>
                {plan.featured && <span className={styles.popularLabel}>Популярный</span>}
                {discount != null && <span className={styles.discountChip}>Выгоднее на {discount}%</span>}
              </span>
              <span className={styles.subtitle}>{copy?.subtitle ?? fallbackSubtitle}</span>
              {copy?.footer && <span className={styles.footerText}>{copy.footer}</span>}
            </button>
          )
        })}
      </div>
      {data.catalogError && <p role="alert">{data.catalogError}</p>}
      {payMessage && <p role={payMessage === 'Покупка подтверждена' ? 'status' : 'alert'}>{payMessage}</p>}
      <button className={styles.details} onClick={() => navigate('/pricing')}>
        Подробнее о тарифах →
      </button>
    </div>
  )
}
